import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, ArrowsClockwise, Trash, Path, Truck, ClipboardText,
  CheckCircle, CaretRight, MapPin, Calendar, FlagCheckered, Crosshair, Camera,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { routesApi, API } from "@/lib/api";

const STATUS_COLORS = {
  planned: "#4682b4",
  dispatched: "#ff8c00",
  in_progress: "#ff8c00",
  completed: "#22c55e",
  cancelled: "#71717a",
};

const STATUS_NEXT = {
  planned: "dispatched",
  dispatched: "in_progress",
  in_progress: "completed",
};

export default function SavedRoutesPage() {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      setRoutes(await routesApi.list({ limit: 200 }));
    } catch (_e) {
      console.warn("Failed to list routes", _e);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const advance = async (route) => {
    const next = STATUS_NEXT[route.status];
    if (!next) return;
    try {
      await routesApi.update(route.id, { status: next });
      toast.success(`Route → ${next.replace("_", " ")}`);
      load();
      if (active?.id === route.id) {
        setActive(await routesApi.get(route.id));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Status update failed");
    }
  };

  const remove = async (route) => {
    if (!window.confirm(`Delete route "${route.name}"? Linked jobs keep their data but lose their dispatch order.`)) return;
    try {
      await routesApi.delete(route.id);
      toast.success("Route deleted");
      if (active?.id === route.id) setActive(null);
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="h-[calc(100vh-72px)] flex" data-testid="saved-routes-page">
      <div className="w-[480px] border-r border-[#2a2a2a] bg-[#0d0d0d] flex flex-col">
        <div className="px-4 py-3 border-b border-[#2a2a2a] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Link to="/routes" className="text-[#71717a] hover:text-[#ff8c00]"><ArrowLeft size={14} weight="bold" /></Link>
            <h2 className="font-heading font-black text-base tracking-tight flex items-center gap-2">
              <Path size={16} className="text-[#ff8c00]" weight="bold" /> Saved Routes
            </h2>
          </div>
          <button onClick={load} className="text-[#71717a] hover:text-[#ff8c00] p-1" title="Refresh" data-testid="saved-routes-refresh">
            <ArrowsClockwise size={12} weight="bold" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading && <div className="px-4 py-6 text-xs text-[#71717a] text-center uppercase tracking-[0.2em]">Loading…</div>}
          {!loading && routes.length === 0 && (
            <div className="px-4 py-6 text-xs text-[#71717a] text-center uppercase tracking-[0.2em]">
              No routes yet — head back to the planner
            </div>
          )}
          {!loading && routes.map((r) => (
            <button
              key={r.id}
              onClick={() => setActive(r)}
              className={`w-full text-left px-4 py-3 border-b border-[#2a2a2a] transition-colors ${active?.id === r.id ? "bg-[#ff8c00]/10" : "hover:bg-[#1a1a1a]"}`}
              data-testid={`saved-route-row-${r.id}`}
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="font-bold text-sm truncate">{r.name}</div>
                <span
                  className="px-1.5 py-0.5 text-[9px] uppercase tracking-[0.2em] font-bold border"
                  style={{ color: STATUS_COLORS[r.status], borderColor: STATUS_COLORS[r.status] }}
                >
                  {r.status}
                </span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717a] font-mono flex items-center gap-2">
                <Calendar size={9} />{r.route_date} · <Truck size={9} />{r.asset_name || "—"} · <ClipboardText size={9} />{r.stops?.length || 0} stops
              </div>
              <div className="text-[10px] text-[#a1a1aa] font-mono mt-0.5">
                {r.total_miles} mi · saved {r.savings_pct?.toFixed?.(1) ?? r.savings_pct}%
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto bg-[#0a0a0a]">
        {!active && (
          <div className="h-full flex items-center justify-center text-[#71717a] uppercase tracking-[0.2em] text-xs font-mono" data-testid="saved-routes-empty">
            Select a route to view stops
          </div>
        )}
        {active && (
          <div className="p-6 max-w-3xl" data-testid="saved-route-detail">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a]">{active.route_date}</div>
                <h1 className="font-heading font-black text-2xl tracking-tight">{active.name}</h1>
                <div className="text-[11px] text-[#a1a1aa] font-mono mt-1">
                  {active.asset_name || "Unassigned"} · {active.total_miles} mi · saved {active.savings_miles} mi ({active.savings_pct}%)
                </div>
              </div>
              <div className="flex items-center gap-2">
                {STATUS_NEXT[active.status] && (
                  <button
                    onClick={() => advance(active)}
                    className="bg-[#ff8c00] hover:bg-[#ff8c00]/90 text-black font-bold px-3 py-2 text-[10px] uppercase tracking-[0.25em] flex items-center gap-1"
                    data-testid="saved-route-advance"
                  >
                    {active.status === "in_progress" ? <FlagCheckered size={11} weight="bold" /> : <CaretRight size={11} weight="bold" />}
                    {active.status === "planned" && "Dispatch"}
                    {active.status === "dispatched" && "Mark In-Progress"}
                    {active.status === "in_progress" && "Complete"}
                  </button>
                )}
                <button
                  onClick={() => remove(active)}
                  className="border border-[#ef4444]/60 text-[#ef4444] hover:bg-[#ef4444]/10 px-3 py-2 text-[10px] uppercase tracking-[0.25em] font-bold flex items-center gap-1"
                  data-testid="saved-route-delete"
                >
                  <Trash size={11} weight="bold" /> Delete
                </button>
              </div>
            </div>

            <div className="space-y-1.5">
              {active.stops.map((s) => {
                const hasGps = s.completed_lat != null && s.completed_lng != null;
                return (
                <div key={s.seq} className="border border-[#2a2a2a] bg-[#0d0d0d] p-3 flex items-start gap-3" data-testid={`saved-route-stop-${s.seq}`}>
                  <div className="w-9 h-9 bg-[#ff8c00] text-black font-mono font-black flex items-center justify-center shrink-0">{s.seq}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm flex items-center gap-2 truncate">
                      {s.job_number ? <span className="text-[#ff8c00] font-mono">{s.job_number}</span> : null}
                      <span className="truncate">{s.customer || s.label}</span>
                      {s.completed_at && <CheckCircle size={12} weight="fill" className="text-[#22c55e] shrink-0" />}
                      {hasGps && <Crosshair size={12} weight="bold" className="text-[#22c55e] shrink-0" title="GPS-stamped completion" data-testid={`saved-stop-gps-${s.seq}`} />}
                      {s.proof_photo_id && <Camera size={12} weight="fill" className="text-[#22c55e] shrink-0" title="POD attached" />}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717a] font-mono flex items-center gap-1 mt-0.5">
                      <MapPin size={9} />{s.lat.toFixed(4)}, {s.lng.toFixed(4)}
                      {s.weight_lbs ? ` · ${(s.weight_lbs / 1000).toFixed(1)}k lbs` : ""}
                      {s.completed_at ? ` · ✓ ${new Date(s.completed_at).toLocaleTimeString()}` : ""}
                    </div>
                    {hasGps && (
                      <a
                        href={`https://maps.google.com/?q=${s.completed_lat},${s.completed_lng}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[10px] text-[#22c55e] hover:text-[#22c55e]/80 hover:underline font-mono flex items-center gap-1 mt-1 w-fit"
                        data-testid={`saved-stop-gps-link-${s.seq}`}
                        title="Open completion coords on Google Maps"
                      >
                        <Crosshair size={9} weight="bold" />
                        completion stamp {s.completed_lat.toFixed(5)}, {s.completed_lng.toFixed(5)}
                        {s.completed_accuracy_m != null ? ` · ±${Math.round(s.completed_accuracy_m)} m` : ""}
                      </a>
                    )}
                  </div>
                  {s.proof_photo_id && (
                    <a
                      href={`${API}/photos/${s.proof_photo_id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="shrink-0 block border border-[#22c55e]/40 hover:border-[#22c55e] transition-colors"
                      title="Open watermarked POD photo"
                      data-testid={`saved-stop-pod-${s.seq}`}
                    >
                      <img
                        src={`${API}/photos/${s.proof_photo_id}`}
                        alt={`POD for stop ${s.seq}`}
                        className="w-20 h-20 object-cover"
                        loading="lazy"
                      />
                    </a>
                  )}
                </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
